/**
 * The held-key match scoreboard.
 *
 * `MatchStatsCollector` has tracked every car's goals, assists, saves, shots
 * and demos since the first tick, and until now the numbers were read exactly
 * once — at full time, to pay progression. This surfaces them DURING the match,
 * which is the only time they can change what a player does.
 *
 * Everything here is pure: rows in, ordered rows and markup out. The HUD owns
 * the element and the key; this owns the ordering, the labels and the MVP
 * decision, and all three are the parts worth testing without a browser.
 *
 * THE MVP SHOWN HERE IS PROVISIONAL. `MatchStatsCollector.mvpIndex` needs a
 * WINNER and there is not one until full time, so mid-match this marks the
 * highest match score on the field regardless of team — which is the question
 * a player is actually asking when they hold the key. `scoreboardRows` says so
 * in the flag's name (`leader`, not `mvp`) rather than promising a result the
 * match has not produced.
 */

import { matchScore } from '../game/match-stats';
// `MatchStats` is the economy's contract, not the collector's — see the header
// of ../game/match-stats, which imports it from here for the same reason.
import type { MatchStats } from '../game/cosmetics';

/**
 * "Is the scoreboard key down right now."
 *
 * A HELD key, not a latch, so it needs both edges — which is why this is not
 * `SkipInput`. Tab is the key players expect, and the browser expects it too:
 * without `preventDefault` the first press moves focus out of the canvas and
 * the second one leaves the page entirely, so the default is suppressed on
 * both edges rather than only on the one we act on.
 *
 * `blur` releases the key: a player who alt-tabs away mid-hold never sends the
 * keyup, and coming back to a scoreboard welded open is worse than one that
 * closed itself.
 */
export class HeldKey {
  private held = false;
  private readonly onDown: (event: Event) => void;
  private readonly onUp: (event: Event) => void;
  private readonly onBlur: () => void;

  constructor(
    private readonly target: EventTarget,
    private readonly code = 'Tab'
  ) {
    this.onDown = (event: Event): void => {
      const key = event as KeyboardEvent;
      if (key.code !== this.code) return;
      key.preventDefault();
      this.held = true;
    };
    this.onUp = (event: Event): void => {
      const key = event as KeyboardEvent;
      if (key.code !== this.code) return;
      key.preventDefault();
      this.held = false;
    };
    this.onBlur = (): void => {
      this.held = false;
    };
    target.addEventListener('keydown', this.onDown);
    target.addEventListener('keyup', this.onUp);
    target.addEventListener('blur', this.onBlur);
  }

  get isHeld(): boolean {
    return this.held;
  }

  dispose(): void {
    this.target.removeEventListener('keydown', this.onDown);
    this.target.removeEventListener('keyup', this.onUp);
    this.target.removeEventListener('blur', this.onBlur);
    this.held = false;
  }
}

/** One car's line on the board, before ordering. */
export interface ScoreboardEntry {
  carIndex: number;
  team: 0 | 1;
  /** Display name. Positional ("You", "Blue 2") offline; a real name online. */
  name: string;
  /** The car the local player is driving, if any. */
  isLocal: boolean;
  stats: MatchStats;
}

/** One rendered line: the entry, its derived score, and its flags. */
export interface ScoreboardRow extends ScoreboardEntry {
  /** Rocket League's match score — the number the ordering is by. */
  score: number;
  /**
   * The single best player ON THE FIELD, not on this row's team. Provisional,
   * and NOT the end-of-match MVP, which is the best player on the WINNING
   * team — see the header. One star per board, never one per side.
   */
  leader: boolean;
}

/**
 * Car index of the highest match score across every entry, or null when
 * nobody has scored yet.
 *
 * Separate from `scoreboardRows` because the board is rendered one TEAM at a
 * time while this question is asked of the whole field: computing it inside
 * the per-team call would silently mark a leader on each side, which reads as
 * a "team MVP" — a thing that does not exist mid-match.
 *
 * A field of all-zeros has no leader. Marking one before anybody has done
 * anything makes car 0 look like it is winning at kickoff.
 */
export const fieldLeader = (entries: readonly ScoreboardEntry[]): number | null => {
  let best = 0;
  let bestIndex: number | null = null;
  for (const entry of entries) {
    const score = matchScore(entry.stats);
    // Strictly greater, so a tie goes to the lower car index rather than to
    // whichever order the caller happened to build the array in.
    if (score > best || (score === best && bestIndex !== null && entry.carIndex < bestIndex)) {
      best = score;
      bestIndex = entry.carIndex;
    }
  }
  return best > 0 ? bestIndex : null;
};

/**
 * Rows, best first.
 *
 * `leaderIndex` is the field-wide answer from `fieldLeader`; pass it through
 * so a per-team render still marks the one player who leads everyone. Omitted,
 * it is computed from `entries` alone — right when the caller really is
 * handing over the whole field.
 *
 * Ties break on car index rather than on the input order, for the same reason
 * `mvpIndex` does: an ordering that depends on how the caller happened to
 * build the array is an ordering that changes when nothing did.
 */
export const scoreboardRows = (
  entries: readonly ScoreboardEntry[],
  leaderIndex: number | null = fieldLeader(entries)
): ScoreboardRow[] =>
  entries
    .map(entry => ({
      ...entry,
      score: matchScore(entry.stats),
      leader: entry.carIndex === leaderIndex,
    }))
    .sort((a, b) => b.score - a.score || a.carIndex - b.carIndex);

/** The columns, in display order. Kept here so the head and the body cannot
 *  disagree about how many there are or what they mean. */
export const SCOREBOARD_COLUMNS: readonly { key: keyof MatchStats; label: string }[] = [
  { key: 'goals', label: 'G' },
  { key: 'assists', label: 'A' },
  { key: 'saves', label: 'Sv' },
  { key: 'shots', label: 'Sh' },
  { key: 'demolitions', label: 'Dm' },
];

const esc = (s: string): string =>
  s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] ?? c);

/**
 * The whole panel.
 *
 * Split by team rather than one combined table: the scoreboard's first job is
 * "are we ahead", and a single list ordered by score answers a different
 * question. Team score comes from the match, not from summing goals — an own
 * goal counts for the team but is not credited to anybody's `goals`, and a
 * board where the two disagree is a board nobody trusts again.
 */
export const scoreboardMarkup = (
  entries: readonly ScoreboardEntry[],
  score: readonly [number, number]
): string => {
  const head =
    `<div class="rw-sb-row rw-sb-head">` +
    `<span class="rw-sb-name"></span>` +
    SCOREBOARD_COLUMNS.map(c => `<span>${esc(c.label)}</span>`).join('') +
    `<span class="rw-sb-score">Pts</span>` +
    `</div>`;

  // Computed once, over the WHOLE field, then handed to both sides — see
  // `fieldLeader`. Doing it inside `side` would put a star on each team.
  const leader = fieldLeader(entries);

  const side = (team: 0 | 1): string => {
    const rows = scoreboardRows(entries.filter(e => e.team === team), leader);
    const body = rows
      .map(row => {
        const classes = ['rw-sb-row'];
        if (row.isLocal) classes.push('you');
        if (row.leader) classes.push('leader');
        return (
          `<div class="${classes.join(' ')}">` +
          `<span class="rw-sb-name">${esc(row.name)}</span>` +
          SCOREBOARD_COLUMNS.map(c => `<span>${row.stats[c.key]}</span>`).join('') +
          `<span class="rw-sb-score">${row.score}</span>` +
          `</div>`
        );
      })
      .join('');
    return (
      `<div class="rw-sb-team" data-team="${team}">` +
      `<div class="rw-sb-team-head">` +
      `<span>${team === 0 ? 'Blue' : 'Orange'}</span>` +
      `<b>${score[team]}</b>` +
      `</div>${head}${body}</div>`
    );
  };

  return side(0) + side(1);
};
